/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from "react"
import "react-phone-number-input/style.css"
import { getCountries } from "react-phone-number-input/input"
import { useIntl } from "gatsby-plugin-intl"
import { useForm, Controller } from "react-hook-form"
import PropTypes from "prop-types"
import en from "react-phone-number-input/locale/en.json"
import examples from "libphonenumber-js/examples.mobile.json"
import { getExampleNumber } from "libphonenumber-js"
import patterns from "utils/patterns"
import CountryFlagDropdown from "components/atoms/CountryFlagDropdown"
import BtnGroup from "components/atoms/BtnGroup"
import {
  StyledForm,
  StyledField,
  StyledInput,
  StyledLabel,
  StyledError,
  StyledPhoneInput,
  StyledPhoneInputCountry,
  StyledCountrySelect,
  flagDropdownStyles,
  StyledUpdateButtonsWrapper,
} from "./Form.styles"

const ERROR_TYPES = {
  PATTERN: "pattern",
  REQUIRED: "required",
}

const Form = ({ sendForm, phoneError }) => {
  const { formatMessage } = useIntl()
  const [country, setCountry] = useState("US")
  const { register, handleSubmit, errors, control, getValues } = useForm()
  const [requiredFields, setRequiredFields] = useState("both")

  const updateButtons = [
    { text: "Email and phone", value: "both" },
    { text: "Email", value: "email" },
  ]
  const handleCountryChange = () => setCountry(getValues("country"))

  const handleRequiredFields = (value) => {
    setRequiredFields(value)
  }

  const emailValidationError = (errorType) => {
    const errorMsg = {
      required: formatMessage({
        id: "subscriptionModal.form.error.emailRequired",
      }),
      pattern: formatMessage({
        id: "subscriptionModal.form.error.emailPattern",
      }),
    }
    return errorMsg[errorType]
  }

  return (
    <>
      <StyledForm
        onSubmit={handleSubmit(sendForm)}
        id="subcriptionForm"
        data-testid="subscribeForm"
      >
        <StyledField>
          <StyledInput
            ref={register}
            name="name"
            id="nameInput"
            placeholder=" "
            iserror={errors.name}
            data-testid="subscribeNameInput"
          />
          <StyledLabel htmlFor="nameInput" data-testid="subscribeNameLabel">
            {formatMessage({ id: "subscriptionModal.form.label.name" })}
          </StyledLabel>
        </StyledField>
        <StyledField>
          <StyledInput
            ref={register({
              required: true,
              validate: {
                pattern: (value) => !!value.toLowerCase().match(patterns.email),
              },
            })}
            name="email"
            placeholder=" "
            id="emailInput"
            iserror={errors.email}
            data-testid="subscribeEmailInput"
          />
          <StyledLabel htmlFor="emailInput" data-testid="subscribeEmailLabel">
            {formatMessage({ id: "subscriptionModal.form.label.email" })}
          </StyledLabel>

          {errors.email && (
            <StyledError data-testid="subscribeEmailError">
              {emailValidationError(errors.email.type)}
            </StyledError>
          )}
        </StyledField>
        <StyledField>
          <StyledPhoneInputCountry>
            <StyledCountrySelect
              defaultValue="US"
              ref={register}
              name="country"
              onChange={handleCountryChange}
              data-testid="subscribeCountrySelect"
            >
              {getCountries().map((countryCode) => (
                <option
                  key={countryCode}
                  value={countryCode}
                  data-testid="subscribeCountrySelectOption"
                >
                  {en[countryCode]}
                </option>
              ))}
            </StyledCountrySelect>
            <CountryFlagDropdown
              control={control}
              extraStyles={flagDropdownStyles}
            />
          </StyledPhoneInputCountry>
          <StyledPhoneInput
            ref={register({
              required: requiredFields !== "email",
            })}
            id="phoneInput"
            name="phone"
            iserror={errors.phone}
            placeholder={getExampleNumber(country, examples).formatNational()}
            data-testid="subscribePhoneInput"
          />
          <StyledLabel htmlFor="phoneInput" data-testid="subscribePhoneLabel">
            {formatMessage({ id: "subscriptionModal.form.label.phone" })}
          </StyledLabel>
          {phoneError && <StyledError>{phoneError}</StyledError>}
          {errors.phone && errors.phone.type === ERROR_TYPES.REQUIRED && (
            <StyledError data-testid="subscribePhoneError">
              {formatMessage({
                id: "subscriptionModal.form.error.phoneRequired",
              })}
            </StyledError>
          )}
        </StyledField>
        <StyledUpdateButtonsWrapper data-testid="subscribeUpdateButtonsWrapper">
          <StyledLabel data-testid="subscribeUpdateButtonsLabel">
            {formatMessage({ id: "subscriptionModal.form.label.updates" })}
          </StyledLabel>
          <Controller
            control={control}
            name="channel"
            defaultValue="both"
            render={({ onChange, name }) => (
              <BtnGroup
                buttonList={updateButtons}
                onChange={(value) => {
                  onChange(value)
                  handleRequiredFields(value)
                }}
                name={name}
              />
            )}
          />
        </StyledUpdateButtonsWrapper>
      </StyledForm>
    </>
  )
}

export default Form

Form.propTypes = {
  sendForm: PropTypes.func.isRequired,
  phoneError: PropTypes.string,
}

Form.defaultProps = {
  phoneError: null,
}
